import { Box, ButtonGroup, Drawer, Grid, IconButton, Tooltip, Zoom } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Create, ThumbDownAlt, ThumbUpAlt } from "@material-ui/icons";
import { useAccessControl, useAuth, useVerifyPermission } from "@airborne/react-admin";
import * as React from "react";
import { FC, Fragment } from "react";
import { Datagrid, ListProps, TextField, useDataProvider, useNotify, useRefresh } from "react-admin";
import { useHistory } from "react-router-dom";
import { DetailButton } from "../../../Components/DetailButton";
import { ConfirmButton } from "../../../Components/ConfirmButton";
import { CustomDateField } from "../../../Components/CustomDateField";
import FilterToolbar from "../../../Components/FiltersToolbar/FilterToolbar";
import CreatedByField from "../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import { DefaultCreatedDateSort, DefaultPerPageSize } from "../../../Constants/Constant";
import { useAppDataProvider } from "../../../dataprovider";
import { FilterInputType } from "../../../Enums/FilterTypes";
import { RequestStatus } from "../../../Enums/RequestStatus";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { RepairProcessDoneListTitle, SourceRequestOrder } from "./Constant/Source";
import RequestOrderNotes from "./RequestOrderNotes";
import { RemarkTypes } from "../../../../src/Enums/RemarkTypes";
import PoolList from "../../../Components/PoolListComponent/PoolListComponent";

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    width: "20%",
  },
}));

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const { remarkAddClick, ...rest } = props;
  const dataProvider = useAppDataProvider();
  const notify = useNotify();
  const provider = useDataProvider();
  const history = useHistory();
  const refresh = useRefresh();
  const { user } = useAuth();
  const { canCreate, canEdit, canRead, hasAccess } = useAccessControl(SourceRequestOrder);

  const acceptButtonClick = record => e => {
    dataProvider.updatePreviousStep(record).then(result => {
      notify(`Record updated`, "success", false, true);
      history.push(`/requestorders/${record.id}/3`);
    });
  };
  const rejectButtonClick = record => e => {
    record.requestStatus = RequestStatus.CancellationRepairPeriod;
    dataProvider.updateRequestOrder(record).then(result => {
      notify(`Record updated`, "success", false, true);
      refresh();
    });
  };
  const remarkOnClick = record => e => {
    e.stopPropagation();
    remarkAddClick?.(record);
  };

  const AcceptCoverageButton = ({ record }) => {
    return (
      <ConfirmButton
        {...props}
        variant="text"
        style={{
          color: "#12824C",
        }}
        size="medium"
        click={acceptButtonClick(record)}
      >
        <Tooltip TransitionComponent={Zoom} title="Accept">
          <ThumbUpAlt />
        </Tooltip>
      </ConfirmButton>
    );
  };
  const RejectCoverageButton = ({ record }) => {
    return (
      <ConfirmButton
        {...props}
        variant="text"
        style={{
          color: "#primary",
        }}
        size="medium"
        click={rejectButtonClick(record)}
      >
        <Tooltip TransitionComponent={Zoom} title="Reject">
          <ThumbDownAlt />
        </Tooltip>
      </ConfirmButton>
    );
  };
  const RemarkButton = ({ record }) => {
    return (
      <IconButton style={{ width: 40 }} onClick={remarkOnClick(record)}>
        <Tooltip TransitionComponent={Zoom} title="Core Last Update">
          <Create />
        </Tooltip>
      </IconButton>
    );
  };
  const DetailsButton = ({ record }) => {
    return <DetailButton record={record} resource={"requestorders"} />;
  };
  const OptButtonGroup = ({ record }) => {
    return (
      <Box style={{ flex: 1, flexDirection: "row" }}>
        <ButtonGroup variant="text" size="small">
          <DetailsButton record={record} />
          {user?.isCustomer === false && canCreate && (
            <>
              <AcceptCoverageButton record={record} />
              <RejectCoverageButton record={record} />
              <RemarkButton record={record} />
            </>
          )}
        </ButtonGroup>
      </Box>
    );
  };

  return (
    <Fragment>
      <Datagrid {...rest} optimized basePath={SourceRequestOrder}>
        <OptButtonGroup record={props} />
        <TextField source="refNo" label="Ref No" />
        <TextField source="componentDescription" label="Description" />
        <CreatedByField label="Created By" link={false} />
        <CustomDateField source="createdDate" label="Created Date" showTime />
        <TextField source="currentPbhNote" label="Current PBH Note" />
      </Datagrid>
    </Fragment>
  );
};

const RepairProcessDoneList: FC<ListProps> = props => {
  useVerifyPermission("customroutes", "repairprocess.read");
  const classes = useStyles();
  const [isMatchCreate, setIsMatchCreate] = React.useState(false);
  const [requestRemark, setRequestRemark] = React.useState<any>({});
  const ExportToExcel = data => {
    ExcelExport(
      [
        {
          header: "Ref No",
          key: "Ref No",
        },
        {
          header: "Description",
          key: "Description",
        },
        {
          header: "Created By",
          key: "Created By",
        },
        {
          header: "Created Date",
          key: "Created Date",
        },
      ],
      data.map(v => [v.refNo, v.componentDescription, v.createdBy, v.createdDate]),
      "Repair Process Will Done"
    );
  };
  const remarkAddClick = record => {
    setIsMatchCreate(true);
    setRequestRemark(record);
  };
  const handleClose = () => {
    setIsMatchCreate(false);
  };
  return (
    <Grid container>
      <Grid item xs={isMatchCreate ? 9 : 12}>
        <PoolList
          {...props}
          hasCreate={false}
          exporter={ExportToExcel}
          bulkActionButtons={false}
          resource={SourceRequestOrder}
          basePath={SourceRequestOrder}
          title={RepairProcessDoneListTitle}
          filterDefaultValues={{
            requestStatus__eq: RequestStatus.OncomingDeliveryDate,
          }}
          sort={DefaultCreatedDateSort}
          perPage={DefaultPerPageSize}
          filters={
            <FilterToolbar
              dataSource={[
                {
                  source: "refNo",
                  inputType: FilterInputType.Text,
                  label: "Ref No",
                },
                {
                  source: "componentDescription",
                  inputType: FilterInputType.Text,
                  label: "Customer Description",
                },
                {
                  source: "createdDate",
                  inputType: FilterInputType.Date,
                  label: "Created Date",
                },
              ]}
            />
          }
        >
          <TabbedDatagrid remarkAddClick={remarkAddClick} />
        </PoolList>
      </Grid>
      {isMatchCreate && (
        <Grid item xs={3}>
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="persistent"
            open={isMatchCreate}
            anchor="right"
            onClose={handleClose}
          >
            {isMatchCreate ? (
              <RequestOrderNotes
                onCancel={handleClose}
                request={requestRemark}
                resource="requestremarks"
                basePath="requestremarks"
                remarkType={RemarkTypes.PbhNote}
              />
            ) : null}
          </Drawer>
        </Grid>
      )}
    </Grid>
  );
};

export default RepairProcessDoneList;
